// @flow
import {
  INITIAL_UT_CERT_TYPE,
  INTERIM_UT_CERT_TYPE,
  RECERT_UT_CERT_TYPE,
  UNIT_TRANSFER_CERT_TYPE,
} from '../constants/affordableCertificationTypes';

/**
 * Same formatting as the New Tenant Events tab (`ManageHudVoucherDetails/NewTenantEvents/hooks.js`):
 * `MOVE_OUT` → `Move Out`, `INTERIM_UT` → `Interim Ut`.
 */
export function capitalSnakeCaseToTitleCase(str: string | null | void): string {
  if (str == null || str === '') {
    return '';
  }
  return String(str)
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .trim();
}

/** `MasterMoveOutCode.code` for Death of sole family member (HUD-52670-A Part 3). */
const MOVE_OUT_CODE_DEATH_OF_SOLE_MEMBER = '4';

// Part 3 prefix for each certification type a unit transfer row can carry: a plain transfer, or
// one combined with another certification (`RECERT_UT`, `INTERIM_UT`, `INITIAL_UT`). Mirrors only
// the four transfer entries of the backend's `CERTIFICATION_TYPE_TO_MAT_CHANGE_CODE`
// (fortress-backend/src/utils/affordable.js), which is the source of truth for this mapping.
const TRANSFER_CERT_TYPE_TO_VOUCHER_PREFIX: { [string]: string } = {
  [UNIT_TRANSFER_CERT_TYPE]: 'UT',
  [RECERT_UT_CERT_TYPE]: 'AR',
  [INTERIM_UT_CERT_TYPE]: 'IR',
  [INITIAL_UT_CERT_TYPE]: 'IC',
};

/**
 * Table cell label for HUD voucher tabs: empty / null → em dash; otherwise same as NTE.
 *
 * A unit transfer is printed on HUD-52670-A Part 3 as two rows — an "-O" row off the old unit and
 * an "-I" row onto the new one — so a row carrying a `transferDirection` shows the form's
 * abbreviation rather than the plain certification type. The prefix depends on which certification
 * the transfer rode along with (`UT`, `AR`, `IR`, `IC`); see `TRANSFER_CERT_TYPE_TO_VOUCHER_PREFIX`.
 * Likewise a death of the sole family member is still a `MOVE_OUT`; only the move-out code tells it
 * apart, so code 4 prints the form's `MO-D`.
 */
export function certificationTypeToHudVoucherTableLabel(
  certificationType: string | null | void,
  transferDirection?: 'OUT' | 'IN' | null,
  moveOutCode?: string | null,
): string {
  const trimmed = String(certificationType || '').trim();
  if (trimmed === '') {
    return '—';
  }
  if (transferDirection === 'OUT' || transferDirection === 'IN') {
    const prefix = TRANSFER_CERT_TYPE_TO_VOUCHER_PREFIX[trimmed];
    // A row like this shouldn't exist, but fall back to the plain label instead of a bare "-O".
    if (prefix == null) {
      return capitalSnakeCaseToTitleCase(trimmed);
    }
    return `${prefix}-${transferDirection === 'OUT' ? 'O' : 'I'}`;
  }
  if (
    trimmed === 'MOVE_OUT' &&
    moveOutCode === MOVE_OUT_CODE_DEATH_OF_SOLE_MEMBER
  ) {
    return 'MO-D';
  }
  return capitalSnakeCaseToTitleCase(trimmed);
}
